"use client"

import { useEffect, useState } from "react"
import { Heart } from "lucide-react"
import { AuthPromptDialog } from "~/components/auth/AuthPromptDialog"
import { trackActivity } from "~/lib/activity-tracking"
import { env } from "~/env"

type EventSaveButtonProps = {
  eventId: string
  onToggle?: (eventId: string, saved: boolean) => void
  save?: boolean
  isAuthenticated?: boolean
  accessToken?: string
}

export default function EventSaveButton({ eventId, onToggle, save, isAuthenticated, accessToken }: EventSaveButtonProps) {
  const [saved, setSaved] = useState(save ?? false)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  // Sync when parent passes updated save prop
  useEffect(() => {
    if (save !== undefined) setSaved(save)
  }, [save])

  // Load saved state: prefer API for authenticated users, fall back to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return
    if (save !== undefined) return // parent already provides state

    if (accessToken) {
      fetch(`${env.NEXT_PUBLIC_API_URL}/events/saved`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((data: { id: string }[]) => {
          setSaved(data.some((e) => e.id === eventId))
        })
        .catch(() => {
          // Fall back to localStorage
          const stored = JSON.parse(localStorage.getItem("savedEvents") || "[]")
          setSaved(stored.includes(eventId))
        })
    } else {
      const stored = JSON.parse(localStorage.getItem("savedEvents") || "[]")
      setSaved(stored.includes(eventId))
    }
  }, [eventId, save, accessToken])

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isAuthenticated === false) {
      setShowAuthPrompt(true)
      return
    }

    const newState = !saved
    setSaved(newState)

    // Update localStorage
    const stored = JSON.parse(localStorage.getItem("savedEvents") || "[]")
    const updated = newState
      ? Array.from(new Set([...stored, eventId]))
      : stored.filter((id: string) => id !== eventId)
    localStorage.setItem("savedEvents", JSON.stringify(updated))

    // Notify parent if needed
    onToggle?.(eventId, newState)

    // Call API for authenticated users
    if (accessToken) {
      try {
        const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/${eventId}/save`, {
          method: newState ? "POST" : "DELETE",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        })
        if (!res.ok) {
          // Revert on failure
          setSaved(!newState)
          const reverted = !newState
            ? Array.from(new Set([...stored, eventId]))
            : stored.filter((id: string) => id !== eventId)
          localStorage.setItem("savedEvents", JSON.stringify(reverted))
        }
      } catch {
        // Revert on error
        setSaved(!newState)
      }
    }

    if (newState) {
      trackActivity('EVENT_SAVED', 'EVENT', eventId, { accessToken, skipDedup: true })
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleSave}
        title={saved ? "Unsave Event" : "Save Event"}
        className="p-2.5 rounded-full hover:bg-[#F7F0E6] transition"
      >
        <Heart
          className={`h-5 w-5 transition ${
            saved ? "text-[#DE866D] fill-[#DE866D]" : "text-[#5E5D59]"
          }`}
        />
      </button>
      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="Save events to your favourites"
        description="Create your free account to keep track of events you love. It only takes a minute."
        icon="❤️"
      />
    </>
  )
}
